import React, { useState } from 'react';
import { motion } from 'motion/react';
import { ShieldCheck, Award, Sparkles, CheckCircle2, Building2, BookOpen, Globe } from 'lucide-react';

interface PartnerLogo {
  id: string;
  name: string;
  category: 'academic' | 'education' | 'selfpub';
  badge: string;
  description: string;
  symbol: string;
  accentColor: string;
}

const PARTNERS: PartnerLogo[] = [
  {
    id: 'oup',
    name: 'Oxford University Press',
    category: 'academic',
    badge: 'JATS/BITS XML Certified',
    description: 'Academic journal typesetting & automated XML structuring',
    symbol: 'OUP',
    accentColor: 'from-blue-600 to-indigo-800'
  },
  {
    id: 'cup',
    name: 'Cambridge University Press',
    category: 'academic',
    badge: 'PDF/UA Accessible',
    description: 'Higher education textbook layout & screen reader compliance',
    symbol: 'CUP',
    accentColor: 'from-amber-600 to-red-800'
  },
  {
    id: 'cengage',
    name: 'Cengage Learning',
    category: 'education',
    badge: 'Higher Ed Courseware',
    description: 'College textbook layout & multi-subject courseware prepress',
    symbol: 'CNG',
    accentColor: 'from-blue-600 to-cyan-800'
  },
  {
    id: 'excelpixels',
    name: 'Excel Pixels Publishing',
    category: 'education',
    badge: 'Prepress & Typesetting',
    description: 'Comprehensive design, book layout & digital prepress formatting',
    symbol: 'EPP',
    accentColor: 'from-indigo-600 to-purple-800'
  },
  {
    id: 'greatminds',
    name: 'Great Minds',
    category: 'education',
    badge: 'K-12 Curriculum Layout',
    description: 'Eureka Math & PhD Science curriculum typesetting & graphics',
    symbol: 'GM',
    accentColor: 'from-amber-600 to-orange-700'
  },
  {
    id: 'carnegie',
    name: 'Carnegie Learning',
    category: 'education',
    badge: 'STEM & Math Prepress',
    description: 'Adaptive learning courseware layout & mathematical composition',
    symbol: 'CL',
    accentColor: 'from-emerald-600 to-teal-800'
  },
  {
    id: 'amplify',
    name: 'Amplify',
    category: 'education',
    badge: 'K-12 Literacy & Science',
    description: 'Early literacy workbooks & interactive science layout production',
    symbol: 'AMP',
    accentColor: 'from-purple-600 to-pink-800'
  },
  {
    id: 'wiley',
    name: 'Wiley Blackwell',
    category: 'academic',
    badge: 'EPUB3 & Reflowable',
    description: 'Digital e-reader formatting & reflowable layout optimization',
    symbol: 'WLY',
    accentColor: 'from-purple-600 to-indigo-900'
  },
  {
    id: 'pearson',
    name: 'Pearson Education',
    category: 'education',
    badge: 'K-12 & Higher Ed',
    description: '4-color illustrated workbook design & vector diagram recreation',
    symbol: 'PRN',
    accentColor: 'from-sky-600 to-blue-800'
  },
  {
    id: 'mcgraw',
    name: 'McGraw Hill',
    category: 'education',
    badge: '4-Color Prepress',
    description: 'Interactive PDF generation & college textbook typesetting',
    symbol: 'MH',
    accentColor: 'from-red-600 to-rose-800'
  },
  {
    id: 'kdp',
    name: 'Amazon KDP Global',
    category: 'selfpub',
    badge: 'Direct Print Ready',
    description: 'Paperback & Kindle Direct Publishing print file preparation',
    symbol: 'KDP',
    accentColor: 'from-amber-500 to-yellow-600'
  }
];

export const TrustedBySlider: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<'all' | 'academic' | 'education' | 'selfpub'>('all');

  const filteredPartners = activeCategory === 'all'
    ? PARTNERS
    : PARTNERS.filter(p => p.category === activeCategory);

  // Duplicate list to create seamless infinite loop effect
  const marqueeList = [...filteredPartners, ...filteredPartners];

  return (
    <section className="py-12 sm:py-16 bg-gradient-to-b from-white via-slate-50 to-blue-50/30 border-y border-slate-200/80 relative overflow-hidden">
      {/* Background Decorative Blur */}
      <div className="absolute top-1/2 left-0 w-72 h-72 bg-blue-300/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 right-0 w-72 h-72 bg-purple-300/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header Header & Social Proof Badges */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8 text-center md:text-left">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-blue-50 border border-blue-200/80 text-blue-900 text-xs font-extrabold uppercase tracking-widest shadow-xs">
              <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
              <span>Project Work & Prepress Production Experience</span>
            </div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-black text-slate-900 tracking-tight">
              Work Experience & Projects Handled Under Renowned Publishers
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 max-w-2xl font-medium">
              Our specialists have hands-on project experience delivering typesetting, 4-color compositing, JATS/BITS XML conversion, and prepress layout for titles and educational materials under these major publisher imprints and organizations.
            </p>
          </div>

          {/* Category Filter Pills */}
          <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-xl border border-slate-200/80 shrink-0 flex-wrap justify-center">
            {[
              { id: 'all', label: 'All Imprints & Projects' },
              { id: 'academic', label: 'Academic & STM' },
              { id: 'education', label: 'K-12 & Higher Ed' },
              { id: 'selfpub', label: 'Self-Publishers' }
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id as any)}
                className={`px-3 py-1.5 rounded-lg text-xs font-extrabold transition-all ${
                  activeCategory === cat.id
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Infinite Scrolling Logo Marquee Container */}
        <div className="relative group overflow-hidden py-4">
          
          {/* Left/Right Vignette Shadows for Fade Effect */}
          <div className="absolute top-0 bottom-0 left-0 w-16 sm:w-28 bg-gradient-to-r from-white via-white/80 to-transparent z-20 pointer-events-none" />
          <div className="absolute top-0 bottom-0 right-0 w-16 sm:w-28 bg-gradient-to-l from-white via-white/80 to-transparent z-20 pointer-events-none" />

          {/* Marquee Track */}
          <div className="flex items-center gap-4 sm:gap-6 animate-marquee group-hover:[animation-play-state:paused] whitespace-nowrap">
            {marqueeList.map((partner, index) => (
              <div
                key={`${partner.id}-${index}`}
                className="inline-flex items-center gap-3.5 px-4 sm:px-5 py-3.5 rounded-2xl bg-white border border-slate-200/90 shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer shrink-0 group/card"
              >
                {/* Logo Badge Icon */}
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${partner.accentColor} text-white font-black text-xs sm:text-sm flex items-center justify-center shadow-xs shrink-0 group-hover/card:scale-105 transition-transform`}>
                  {partner.symbol}
                </div>

                {/* Partner Details */}
                <div className="flex flex-col text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm font-extrabold text-slate-900 group-hover/card:text-blue-700 transition-colors">
                      {partner.name}
                    </span>
                    <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-100 text-[10px] font-bold">
                      {partner.badge}
                    </span>
                    <span className="text-[11px] text-slate-400 hidden sm:inline">•</span>
                    <span className="text-[11px] text-slate-500 hidden lg:inline max-w-[180px] truncate">
                      {partner.description}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>

        {/* Framing & Experience Disclaimer Note */}
        <div className="mt-4 mb-2 text-center">
          <p className="text-[11px] text-slate-500 font-medium italic max-w-3xl mx-auto">
            * Note: Reflects project-level prepress typesetting, layout composition, JATS XML structuring, and artwork production performed by our specialists for titles and materials under these publisher imprints & guidelines.
          </p>
        </div>

        {/* Bottom Social Proof Metrics Bar */}
        <div className="mt-8 pt-6 border-t border-slate-200/80 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="p-3 rounded-xl bg-white/80 border border-slate-100">
            <p className="text-xl sm:text-2xl font-black text-blue-900">1,220+</p>
            <p className="text-[11px] sm:text-xs text-slate-600 font-bold mt-0.5">Books & Journals Delivered</p>
          </div>
          <div className="p-3 rounded-xl bg-white/80 border border-slate-100">
            <p className="text-xl sm:text-2xl font-black text-indigo-900">100%</p>
            <p className="text-[11px] sm:text-xs text-slate-600 font-bold mt-0.5">WCAG PDF/UA Compliance</p>
          </div>
          <div className="p-3 rounded-xl bg-white/80 border border-slate-100">
            <p className="text-xl sm:text-2xl font-black text-purple-900">3-5 Days</p>
            <p className="text-[11px] sm:text-xs text-slate-600 font-bold mt-0.5">Average SLA Turnaround</p>
          </div>
          <div className="p-3 rounded-xl bg-white/80 border border-slate-100">
            <p className="text-xl sm:text-2xl font-black text-emerald-900">18+</p>
            <p className="text-[11px] sm:text-xs text-slate-600 font-bold mt-0.5">Global Partner Houses</p>
          </div>
        </div>

      </div>
    </section>
  );
};
